// MaterialGraphEditor — ReactFlow canvas for editing material shader graphs.
// Props: { graph, onChange }
// - graph: { version, nodes: [{id, type, data, position}], edges: [{id, source, sourceHandle, target, targetHandle}] }
// - onChange: (normalizedGraph) => void

import React, { useCallback, useMemo, useState, useRef, useEffect } from 'react'
import {
  ReactFlow,
  Background,
  Controls,
  BaseEdge,
  EdgeLabelRenderer,
  getBezierPath,
  addEdge,
  applyNodeChanges,
  applyEdgeChanges,
  useNodesState,
  useEdgesState,
  useReactFlow,
  ReactFlowProvider,
  Handle,
  Position,
} from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import { NODE_TYPES } from '../../lib/materialNodes'
import { getCSSVars, pinColor } from './materialGraphTheme'
import MaterialGraphNode from './MaterialGraphNode'
import MaterialGraphPalette from './MaterialGraphPalette'
import MaterialGraphDetails from './MaterialGraphDetails'
import AnchoredOverlay from '../AnchoredOverlay'
import './materialGraph.css'

// ─── Context menu — shown on right-click in the canvas ───────────────────────
function ContextMenu({ screenX, screenY, onAdd, onClose }) {
  const [search, setSearch] = useState('')
  const inputRef = useRef(null)

  useEffect(() => { inputRef.current?.focus() }, [])

  const palette = Object.entries(NODE_TYPES)
    .filter(([, def]) => !def.isOutput)
    .map(([key, def]) => ({
      type: key,
      label: def.label,
    }))

  const filtered = palette.filter(n =>
    n.label.toLowerCase().includes(search.toLowerCase())
  )

  const menuWidth = 210

  return (
    <AnchoredOverlay
      x={screenX} y={screenY} margin={8}
      style={{
        width: menuWidth,
        background: 'var(--panel)',
        border: '1px solid var(--line-strong)',
        borderRadius: 6,
        boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
        zIndex: 1000,
        fontFamily: 'var(--m-font)',
        maxHeight: 420,
        display: 'flex',
        flexDirection: 'column',
      }}
      onMouseDown={e => e.stopPropagation()}
      onClick={e => e.stopPropagation()}
    >
      <div style={{ padding: '8px 8px 6px', borderBottom: '1px solid var(--line)', flexShrink: 0 }}>
        <input
          ref={inputRef}
          value={search}
          onChange={e => setSearch(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Escape') onClose()
            if (e.key === 'Enter' && filtered.length > 0) { onAdd(filtered[0].type); onClose() }
          }}
          placeholder="Search..."
          style={{
            width: '100%', boxSizing: 'border-box',
            background: 'var(--slot-bg)', border: '1px solid var(--line-strong)',
            color: 'var(--fg)', padding: '4px 8px', borderRadius: 4,
            fontSize: 11, outline: 'none', fontFamily: 'inherit',
          }}
          onFocus={e => e.target.style.borderColor = 'var(--accent)'}
          onBlur={e => e.target.style.borderColor = 'var(--line-strong)'}
        />
      </div>

      <div style={{ overflowY: 'auto', padding: '4px 0' }}>
        {filtered.length === 0 && (
          <div style={{ color: 'var(--fg-3)', fontSize: 11, padding: '10px 12px' }}>No results</div>
        )}
        {filtered.map(item => (
          <div
            key={item.type}
            onClick={() => { onAdd(item.type); onClose() }}
            style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '6px 10px 6px 22px',
              cursor: 'pointer',
              background: 'transparent',
              transition: 'background 0.12s, color 0.12s',
              color: 'var(--fg-2)',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.background = 'var(--panel-2)'
              e.currentTarget.style.color = 'var(--fg)'
            }}
            onMouseLeave={e => {
              e.currentTarget.style.background = 'transparent'
              e.currentTarget.style.color = 'var(--fg-2)'
            }}
          >
            <span style={{ fontSize: 11 }}>{item.label}</span>
          </div>
        ))}
      </div>
    </AnchoredOverlay>
  )
}

// ─── Node right-click menu ────────────────────────────────────────────────────
function NodeMenuItem({ label, color, onClick }) {
  return (
    <div
      onClick={onClick}
      style={{ padding: '7px 12px', cursor: 'pointer', color: color || 'var(--fg-2)', borderBottom: '1px solid var(--line)', transition: 'all 0.12s' }}
      onMouseEnter={e => { e.currentTarget.style.background = 'var(--panel-2)'; if (!color) e.currentTarget.style.color = 'var(--fg)' }}
      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; if (!color) e.currentTarget.style.color = 'var(--fg-2)' }}
    >{label}</div>
  )
}

function NodeContextMenu({ screenX, screenY, nodeId, deletable, paramable, isParam, onDelete, onDuplicate, onConvertParam, onClose }) {
  return (
    <AnchoredOverlay
      x={screenX} y={screenY}
      style={{
        width: 170,
        background: 'var(--panel)', border: '1px solid var(--line-strong)',
        borderRadius: 5, boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
        zIndex: 1000, fontFamily: 'var(--m-font)', fontSize: 11, overflow: 'hidden',
      }}
      onMouseDown={e => e.stopPropagation()}
    >
      <NodeMenuItem label="Duplicate" onClick={() => { onDuplicate(nodeId); onClose() }} />
      {paramable && (
        <NodeMenuItem
          label={isParam ? 'Convert to Constant' : 'Convert to Parameter'}
          color="var(--accent-2, #b7a2ff)"
          onClick={() => { onConvertParam(nodeId, !isParam); onClose() }}
        />
      )}
      {deletable !== false && (
        <div
          onClick={() => { onDelete(nodeId); onClose() }}
          style={{
            padding: '7px 12px', cursor: 'pointer', color: '#e06060',
            transition: 'all 0.12s',
          }}
          onMouseEnter={e => e.currentTarget.style.background = 'var(--panel-2)'}
          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
        >
          Delete
        </div>
      )}
    </AnchoredOverlay>
  )
}

// ── Custom edge: white when selected + a ✕ delete button (no confirm) ─────────
// Deletion routes through the editor's onEdgesChange (passed as onDelete) so it
// persists via commitGraph — not useReactFlow().setEdges, which would bypass it.
function MaterialGraphEdge({
  id, sourceX, sourceY, targetX, targetY,
  sourcePosition, targetPosition, selected, markerEnd, style, onDelete,
}) {
  const [edgePath, labelX, labelY] = getBezierPath({
    sourceX, sourceY, sourcePosition, targetX, targetY, targetPosition,
  })
  const remove = (e) => { e?.stopPropagation?.(); onDelete?.(id) }
  const baseStroke = style?.stroke || 'var(--fg-2)'
  return (
    <>
      <BaseEdge
        path={edgePath}
        markerEnd={markerEnd}
        style={{
          ...style,
          stroke: selected ? '#ffffff' : baseStroke,
          strokeWidth: selected ? 3 : 2,
          opacity: selected ? 1 : (style?.opacity ?? 0.7),
          filter: selected ? 'drop-shadow(0 0 4px rgba(255,255,255,0.8))' : 'none',
        }}
      />
      <EdgeLabelRenderer>
        <div
          style={{
            position: 'absolute',
            transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
            // Only capture clicks when shown — an opacity:0 button with
            // pointerEvents:'all' still intercepts clicks at the edge midpoint,
            // which blocked selection and caused accidental deletes.
            pointerEvents: selected ? 'all' : 'none',
            opacity: selected ? 1 : 0,
            transition: 'opacity 0.12s',
          }}
        >
          <button
            onClick={remove}
            onMouseDown={e => e.stopPropagation()}
            title="Disconnect (or Ctrl/Cmd-click the wire)"
            style={{
              width: 18, height: 18, borderRadius: '50%',
              background: '#c03020', border: '1px solid #ff6050',
              color: '#fff', fontSize: 12, lineHeight: '16px',
              cursor: 'pointer', padding: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >×</button>
        </div>
      </EdgeLabelRenderer>
    </>
  )
}

// ── Canvas component (inside ReactFlowProvider, uses useReactFlow) ────────────
function MaterialGraphCanvas({
  nodes, setNodes, edges, setEdges,
  onNodesChange, onEdgesChange, onConnect,
  addNodeAt, onDeleteNode, onDuplicateNode, onConvertParam,
  updateNodeData, commitGraph,
  selectedNodeId, onSelectNode,
  onZoomChange,
}) {
  const { screenToFlowPosition } = useReactFlow()
  const [contextMenu, setContextMenu] = useState(null)
  const [nodeMenu, setNodeMenu] = useState(null)

  const onPaneContextMenu = useCallback((e) => {
    e.preventDefault()
    setNodeMenu(null)
    const flowPos = screenToFlowPosition({ x: e.clientX, y: e.clientY })
    setContextMenu({ screenX: e.clientX, screenY: e.clientY, flowPos })
  }, [screenToFlowPosition])

  const closeMenu = useCallback(() => setContextMenu(null), [])
  const closeNodeMenu = useCallback(() => setNodeMenu(null), [])

  const onNodeContextMenu = useCallback((e, node) => {
    e.preventDefault()
    e.stopPropagation()
    setContextMenu(null)
    setNodeMenu({
      screenX: e.clientX, screenY: e.clientY, nodeId: node.id,
      deletable: node.deletable !== false,
      paramable: !!NODE_TYPES[node.data?.nodeType]?.paramable,
      isParam: !!node.data?.param,
    })
  }, [])

  const handleAdd = useCallback((type) => {
    if (contextMenu) addNodeAt(type, contextMenu.flowPos)
  }, [contextMenu, addNodeAt])

  // nodeTypes MUST be stable — if it changes every render, ReactFlow remounts
  // every node (un-draggable, inputs lose focus) and the remounts fire endless
  // `dimensions` changes → PATCH flood. Keep it `[]`-stable; reach the latest
  // updateNodeData via a ref, and use ReactFlow's built-in `selected` prop for
  // the is-selected styling instead of threading selectedNodeId through here.
  const updateNodeDataRef = useRef(updateNodeData)
  updateNodeDataRef.current = updateNodeData
  const nodeTypes = useMemo(() => ({
    mgNode: (props) => (
      <MaterialGraphNode
        {...props}
        updateNodeData={(handle, value) => updateNodeDataRef.current(props.id, handle, value)}
      />
    ),
  }), [])

  // Stable edgeTypes (same rationale as nodeTypes). Delete routes through the
  // editor's onEdgesChange so it persists; reach the latest via a ref.
  const onEdgesChangeRef = useRef(onEdgesChange)
  onEdgesChangeRef.current = onEdgesChange
  const removeEdge = useCallback((edgeId) => {
    onEdgesChangeRef.current([{ id: edgeId, type: 'remove' }])
  }, [])
  const edgeTypes = useMemo(() => ({
    mgEdge: (props) => <MaterialGraphEdge {...props} onDelete={removeEdge} />,
  }), [removeEdge])

  // Ctrl/Cmd-click a wire removes it immediately (no confirm).
  const onEdgeClick = useCallback((e, edge) => {
    if (e.ctrlKey || e.metaKey) { e.stopPropagation(); removeEdge(edge.id) }
  }, [removeEdge])

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        position: 'relative',
      }}
      onClick={() => { closeMenu(); closeNodeMenu(); onSelectNode(null) }}
    >
      <ReactFlow
        nodes={nodes}
        edges={useMemo(() => {
          return edges.map(edge => {
            const sourceNode = nodes.find(n => n.id === edge.source)
            const registry = sourceNode && NODE_TYPES[sourceNode.data.nodeType]
            const sourceOutput = registry?.outputs?.find(o => o.handle === edge.sourceHandle)
            const color = sourceOutput ? pinColor(sourceOutput.type) : 'var(--fg-2)'
            // type: 'mgEdge' renders the custom edge (white-on-select + ✕ delete).
            return { ...edge, type: 'mgEdge', style: { stroke: color, strokeWidth: 2, opacity: 0.7 } }
          })
        }, [edges, nodes])}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onPaneContextMenu={onPaneContextMenu}
        onNodeContextMenu={onNodeContextMenu}
        onNodeClick={(e, node) => { e.stopPropagation(); onSelectNode(node.id) }}
        onEdgeClick={onEdgeClick}
        onMove={(_, viewport) => { if (onZoomChange) onZoomChange(viewport.zoom) }}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        // Disconnect a wire by selecting it + Delete/Backspace, or double-click
        // it. ReactFlow's default deleteKeyCode is Backspace ONLY — listing both
        // so the Delete key (what most users press) works too.
        deleteKeyCode={['Backspace', 'Delete']}
        onEdgeDoubleClick={(e, edge) => { e.stopPropagation(); onEdgesChange([{ id: edge.id, type: 'remove' }]) }}
        fitView
      >
        <Background gap={24} style={{ opacity: 0.3 }} />
        <Controls />
      </ReactFlow>

      {contextMenu && (
        <ContextMenu
          screenX={contextMenu.screenX}
          screenY={contextMenu.screenY}
          onAdd={handleAdd}
          onClose={closeMenu}
        />
      )}
      {nodeMenu && (
        <NodeContextMenu
          screenX={nodeMenu.screenX}
          screenY={nodeMenu.screenY}
          nodeId={nodeMenu.nodeId}
          deletable={nodeMenu.deletable}
          paramable={nodeMenu.paramable}
          isParam={nodeMenu.isParam}
          onDelete={onDeleteNode}
          onDuplicate={onDuplicateNode}
          onConvertParam={onConvertParam}
          onClose={closeNodeMenu}
        />
      )}
    </div>
  )
}

// ── Main editor ───────────────────────────────────────────────────────────

export default function MaterialGraphEditor({ graph, onChange, onZoomChange }) {
  const [selectedNodeId, setSelectedNodeId] = useState(null)

  const initialNodes = useMemo(() => {
    if (!graph || !graph.nodes) {
      return [{
        id: 'out',
        type: 'mgNode',
        position: { x: 360, y: 160 },
        data: { nodeType: 'PrincipledBSDF', nodeId: 'out' },
        deletable: false,
      }]
    }

    return graph.nodes.map(n => ({
      id: n.id,
      type: 'mgNode',
      position: n.position ?? { x: 0, y: 0 },
      data: { nodeType: n.type, nodeId: n.id, ...n.data },
      deletable: !NODE_TYPES[n.type]?.isOutput,
    }))
  }, [graph])

  const initialEdges = useMemo(() => {
    if (!graph || !graph.edges) return []
    return graph.edges.map(e => ({
      id: e.id,
      source: e.source,
      sourceHandle: e.sourceHandle,
      target: e.target,
      targetHandle: e.targetHandle,
    }))
  }, [graph])

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes)
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges)

  const commitGraph = useCallback((newNodes, newEdges) => {
    const normalized = {
      version: 1,
      nodes: newNodes.map(n => {
        const { nodeType, nodeId, ...inlineData } = n.data
        return {
          id: n.id,
          type: nodeType,
          data: inlineData,
          position: n.position,
        }
      }),
      edges: newEdges.map(e => ({
        id: e.id,
        source: e.source,
        sourceHandle: e.sourceHandle,
        target: e.target,
        targetHandle: e.targetHandle,
      })),
    }
    onChange(normalized)
  }, [onChange])

  const handleNodesChange = useCallback((changes) => {
    // Hot path: feed the change through ReactFlow's functional updater. Do NOT
    // call commitGraph (a parent setState) inside the updater — React flags
    // that as "setState while rendering".
    setNodes(nds => applyNodeChanges(changes, nds))
    // Persist ONLY on meaningful changes — a node removed, or a drag that just
    // ended (dragging === false). Skip the continuous `dimensions`/`select`/
    // in-flight `position` churn that otherwise floods the server with PATCHes.
    const persist = changes.some(c =>
      c.type === 'remove' || (c.type === 'position' && c.dragging === false))
    if (persist) commitGraph(applyNodeChanges(changes, nodes), edges)
  }, [setNodes, nodes, edges, commitGraph])

  const handleEdgesChange = useCallback((changes) => {
    setEdges(eds => applyEdgeChanges(changes, eds))
    if (changes.some(c => c.type === 'remove')) {
      commitGraph(nodes, applyEdgeChanges(changes, edges))
    }
  }, [setEdges, nodes, edges, commitGraph])

  const handleConnect = useCallback((connection) => {
    const newEdge = {
      id: `${connection.source}:${connection.sourceHandle}-${connection.target}:${connection.targetHandle}`,
      source: connection.source,
      sourceHandle: connection.sourceHandle,
      target: connection.target,
      targetHandle: connection.targetHandle,
    }
    // An input pin takes exactly one link — mirror Unreal's MaterialGraphSchema
    // (CONNECT_RESPONSE_BREAK_OTHERS on the input): drop any edge already wired
    // into this target handle before adding the new one. Outputs still fan out.
    const pruned = edges.filter(e =>
      !(e.target === connection.target && e.targetHandle === connection.targetHandle))
    const newEdges = addEdge(newEdge, pruned)
    setEdges(newEdges)
    commitGraph(nodes, newEdges)
  }, [nodes, edges, setEdges, commitGraph])

  const updateNodeData = useCallback((nodeId, handle, value) => {
    if (handle === '__nodeType__') {
      // Swap the output node's lighting model (Blender closure swap). Seed the
      // new type's input defaults for handles not already present, and prune
      // edges targeting handles the new type doesn't have.
      const def = NODE_TYPES[value]
      const newHandles = new Set((def?.inputs ?? []).map(i => i.handle))
      const newNodes = nodes.map(n => {
        if (n.id !== nodeId) return n
        const seeded = { ...n.data, nodeType: value }
        for (const inp of def?.inputs ?? []) {
          if (!(inp.handle in seeded)) seeded[inp.handle] = inp.default
        }
        return { ...n, data: seeded }
      })
      const newEdges = edges.filter(e => !(e.target === nodeId && !newHandles.has(e.targetHandle)))
      setNodes(newNodes)
      setEdges(newEdges)
      commitGraph(newNodes, newEdges)
      return
    }
    const newNodes = nodes.map(n =>
      n.id === nodeId
        ? { ...n, data: handle === '__expose__' ? { ...n.data, expose: value } : { ...n.data, [handle]: value } }
        : n
    )
    setNodes(newNodes)
    commitGraph(newNodes, edges)
  }, [nodes, setNodes, edges, setEdges, commitGraph])

  const addNodeAt = useCallback((nodeType, flowPos) => {
    const id = `${nodeType}_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`
    // The left palette calls this without a position; fall back to a cascading
    // spot near the top-left so ReactFlow always gets a valid {x,y} (an
    // undefined position throws in getNodePositionWithOrigin).
    const position = flowPos ?? { x: 80 + (nodes.length % 6) * 28, y: 80 + (nodes.length % 6) * 28 }
    const newNode = {
      id,
      type: 'mgNode',
      position,
      data: {
        nodeType,
        nodeId: id,
        ...Object.fromEntries(
          (NODE_TYPES[nodeType]?.inputs ?? [])
            .map(input => [input.handle, input.default])
        ),
      },
      deletable: !NODE_TYPES[nodeType]?.isOutput,
    }
    const newNodes = [...nodes, newNode]
    setNodes(newNodes)
    commitGraph(newNodes, edges)
  }, [nodes, edges, setNodes, commitGraph])

  const onDeleteNode = useCallback((nodeId) => {
    const newNodes = nodes.filter(n => n.id !== nodeId)
    const newEdges = edges.filter(e => e.source !== nodeId && e.target !== nodeId)
    setNodes(newNodes)
    setEdges(newEdges)
    if (selectedNodeId === nodeId) setSelectedNodeId(null)
    commitGraph(newNodes, newEdges)
  }, [nodes, edges, setNodes, setEdges, selectedNodeId, commitGraph])

  const onDuplicateNode = useCallback((nodeId) => {
    const nodeToClone = nodes.find(n => n.id === nodeId)
    if (!nodeToClone) return

    const newId = `${nodeToClone.data.nodeType}_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`
    const newNode = {
      ...nodeToClone,
      id: newId,
      position: { x: nodeToClone.position.x + 50, y: nodeToClone.position.y + 50 },
      data: { ...structuredClone(nodeToClone.data), nodeId: newId },
    }
    const newNodes = [...nodes, newNode]
    setNodes(newNodes)
    commitGraph(newNodes, edges)
  }, [nodes, edges, setNodes, commitGraph])

  // Unreal-style Convert to Parameter / Convert to Constant on a node. Converting
  // gives a generic, auto-unique name (Param, Param 2, …); the user can rename
  // two nodes to the same name to make them the SAME parameter.
  const onConvertParam = useCallback((nodeId, toParam) => {
    const existing = new Set(nodes.filter(x => x.data?.param && x.id !== nodeId).map(x => x.data?.paramName))
    let name = 'Param', i = 1
    while (existing.has(name)) { i += 1; name = `Param ${i}` }
    const newNodes = nodes.map(n => {
      if (n.id !== nodeId) return n
      if (toParam) {
        return { ...n, data: { ...n.data, param: true, paramName: n.data.paramName || name } }
      }
      const { param, paramName, ...rest } = n.data
      return { ...n, data: rest }
    })
    setNodes(newNodes)
    commitGraph(newNodes, edges)
  }, [nodes, edges, setNodes, commitGraph])

  const cssVars = getCSSVars()

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '218px 1fr 268px',
        height: '100%',
        background: 'var(--canvas)',
        color: 'var(--fg)',
        fontFamily: 'var(--m-font)',
        ...cssVars,
      }}
    >
      {/* Left: Palette */}
      <MaterialGraphPalette onAddNode={addNodeAt} />

      {/* Center: Canvas */}
      <div style={{ position: 'relative', overflow: 'hidden' }}>
        <ReactFlowProvider>
          <MaterialGraphCanvas
            nodes={nodes}
            setNodes={setNodes}
            edges={edges}
            setEdges={setEdges}
            onNodesChange={handleNodesChange}
            onEdgesChange={handleEdgesChange}
            onConnect={handleConnect}
            addNodeAt={addNodeAt}
            onDeleteNode={onDeleteNode}
            onDuplicateNode={onDuplicateNode}
            onConvertParam={onConvertParam}
            updateNodeData={updateNodeData}
            commitGraph={commitGraph}
            selectedNodeId={selectedNodeId}
            onSelectNode={setSelectedNodeId}
            onZoomChange={onZoomChange}
          />
        </ReactFlowProvider>
      </div>

      {/* Right: Details panel */}
      <MaterialGraphDetails
        graph={graph}
        nodes={nodes}
        edges={edges}
        selectedNodeId={selectedNodeId}
        onSelectNode={setSelectedNodeId}
        updateNodeData={updateNodeData}
      />
    </div>
  )
}
